import { api } from './client'
import type { PaginatedResponse } from './organizations'

// ---- Types ----

export type { PaginatedResponse }

export interface OrgUser {
  id: string
  name: string
  email: string
  phone: string | null
  role_slug: string | null
  role_display_name: string | null
  is_active: boolean
  last_login: string | null
  created_at: string
}

export interface OrgUserDetail extends OrgUser {
  updated_at: string
  organization_id: string
}

export interface ListUsersParams {
  page?: number
  per_page?: number
  search?: string
  role_slug?: string
  is_active?: boolean | null
  sort_by?: string
  sort_order?: 'asc' | 'desc'
}

export interface ProvisionUserRequest {
  email: string
  name: string
  role_slug: string
  phone?: string
  send_welcome_email?: boolean
}

export interface UpdateUserRequest {
  name?: string
  email?: string
  phone?: string | null
  role_slug?: string
}

export interface ReactivateUserRequest {
  role_slug: string
}

export interface ProvisionUserResponse {
  user: OrgUserDetail
  invite_email_sent: boolean
}

export interface EmailCheckResponse {
  email: string
  available: boolean
}

export interface OrgRole {
  slug: string
  display_name: string
  is_supervisor: boolean
}

// ---- API functions ----

function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== '') {
      searchParams.set(key, String(value))
    }
  }
  return searchParams.toString()
}

export const userApi = {
  list: (orgId: string, params: ListUsersParams = {}) =>
    api.get<PaginatedResponse<OrgUser>>(
      `/api/v1/admin/platform/organizations/${orgId}/users?${buildQueryString(params as Record<string, unknown>)}`
    ),

  detail: (orgId: string, userId: string) =>
    api.get<OrgUserDetail>(
      `/api/v1/admin/platform/organizations/${orgId}/users/${userId}`
    ),

  provision: (orgId: string, data: ProvisionUserRequest) =>
    api.post<ProvisionUserResponse>(
      `/api/v1/admin/platform/organizations/${orgId}/users`,
      data
    ),

  update: (orgId: string, userId: string, data: UpdateUserRequest) =>
    api.patch<OrgUserDetail>(
      `/api/v1/admin/platform/organizations/${orgId}/users/${userId}`,
      data
    ),

  deactivate: (orgId: string, userId: string) =>
    api.post<{ user_id: string; deactivated: boolean }>(
      `/api/v1/admin/platform/organizations/${orgId}/users/${userId}/deactivate`
    ),

  reactivate: (orgId: string, userId: string, data: ReactivateUserRequest) =>
    api.post<{ user_id: string; reactivated: boolean }>(
      `/api/v1/admin/platform/organizations/${orgId}/users/${userId}/reactivate`,
      data
    ),

  checkEmail: (email: string, excludeUserId?: string) => {
    const params = excludeUserId ? `?exclude_user_id=${excludeUserId}` : ''
    return api.get<EmailCheckResponse>(
      `/api/v1/admin/platform/users/check-email/${encodeURIComponent(email)}${params}`
    )
  },

  listRoles: (orgId: string) =>
    api.get<OrgRole[]>(
      `/api/v1/admin/platform/organizations/${orgId}/roles`
    ),
}
